/**
 * `/mail` — layout for the unified mail-and-calendar surface.
 *
 * Per founder direction: Calendar is part of Mail, not a sibling. One
 * launchpad tile ("Mail") opens this surface; a sub-nav inside swaps
 * between the Inbox (Mail) and Calendar panes.
 *
 * Pattern matches Proton Mail + Outlook + Gmail-with-Calendar-side-
 * panel: the suite is a single feature in the AdminShell; the panes
 * inside are sibling URLs (`/mail` for inbox, `/mail/calendar` for
 * calendar) that share this layout.
 *
 * Joint-project: the underlying components still come from two
 * independent packages — `@postopia/mail-ui` and
 * `@postopia/calendar-ui` — composed at the host level. MyVigator
 * can do the same composition in their own bundle.
 */

import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from '@tanstack/react-router';
import { Glyph } from '@postopia/suite-shell';
import { AdminShell } from '../components/AdminShell';

export const Route = createFileRoute('/mail')({
  component: MailLayout,
});

function MailLayout() {
  const { pathname } = useLocation();
  // Calendar pane is active iff URL contains /calendar.
  const isCalendar = pathname.startsWith('/mail/calendar');
  const activePane: 'mail' | 'calendar' = isCalendar ? 'calendar' : 'mail';
  return (
    <AdminShell
      mode="feature"
      featureName={isCalendar ? 'Mail · Calendar' : 'Mail'}
      topBarActions={<ExternalClientsButton />}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <SuiteNav active={activePane} />
        <div
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Outlet />
        </div>
        {/*
          Mobile bottom-tab nav. Lives at the suite layout level
          (NOT inside the Mail or Calendar component) so it shows
          identically on both panes without each pane having to
          plumb the prop. Hidden on md+ via Tailwind `md:hidden`.
          Flex-sibling instead of position:fixed — content area
          already shrinks to leave room because it's flex:1.
        */}
        <MobileSuiteNav active={activePane} />
      </div>
    </AdminShell>
  );
}

/**
 * Topbar shortcut → External mail clients (IMAP/SMTP app passwords).
 * Mirrors MyVigator's key-icon in the same slot — the host topbar is
 * the natural place for a "set up Apple Mail / Outlook" shortcut so
 * users don't have to dig through /settings to find it.
 */
function ExternalClientsButton() {
  return (
    <Link
      to="/settings/mail"
      aria-label="External mail clients (IMAP / SMTP)"
      title="External mail clients (IMAP / SMTP)"
      style={{
        width: 32,
        height: 32,
        borderRadius: 4,
        border: '1px solid var(--c-border-hairline)',
        background: 'transparent',
        color: 'var(--c-fg-secondary)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        textDecoration: 'none',
      }}
    >
      <Glyph name="key" size={14} style={{ color: 'currentColor' }} />
    </Link>
  );
}

function SuiteNav({ active }: { active: 'mail' | 'calendar' }) {
  return (
    <nav
      aria-label="Mail suite panes"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: '4px 12px',
        borderBottom: '1px solid var(--c-border-hairline)',
        background: 'var(--c-bg-surface)',
        flexShrink: 0,
      }}
    >
      <SuitePill
        to="/mail"
        icon="inbox"
        label="Mail"
        isActive={active === 'mail'}
      />
      <SuitePill
        to="/mail/calendar"
        icon="calendar"
        label="Calendar"
        isActive={active === 'calendar'}
      />
    </nav>
  );
}

/**
 * MobileSuiteNav — sticky bottom tab bar visible only on mobile
 * (< md / < 768px). Shows on every `/mail/*` route via this parent
 * layout, so it's identical on Mail and Calendar.
 *
 * Why not use mail-ui's `mobileBottomNav` prop:
 *   - That prop renders inside the Mail component, so it'd only show
 *     on `/mail`, not on `/mail/calendar` (which uses Calendar, a
 *     different package).
 *   - Adding the same prop to calendar-ui would mean two parallel
 *     implementations to maintain.
 *   - Living at the route layout level = single source of truth for
 *     cross-pane chrome.
 *
 * MyV's bundle is free to keep using the mail-ui prop; this is
 * the Postopia host's choice to render at the layout level.
 */
function MobileSuiteNav({ active }: { active: 'mail' | 'calendar' }) {
  return (
    <nav
      // display lives in the CSS class, NOT inline: an inline display:flex
      // outranked md:hidden, so this phone bar showed on desktop under the
      // top Mail/Calendar tabs (CLAUDE.md §0g; Q audit 2026-09-24).
      className="q-suite-nav"
      aria-label="Suite features"
      style={{
        height: 56,
        flexShrink: 0,
        // Respect notch / iOS home indicator. Host's viewport meta
        // tag must include `viewport-fit=cover` for env() to resolve.
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        background: 'var(--c-bg-surface)',
        borderTop: '1px solid var(--c-border-hairline)',
        // Chrome outranks scrolling content. Without a position+z-index this
        // bar is an in-flow element with no stacking of its own, so anything
        // POSITIONED inside the panes above paints over it — which is how the
        // mail list's sticky "Yesterday" date strip ended up scrolling across
        // these icons. mail-ui now isolates its own scroller so it cannot
        // escape, and this makes the bar win regardless of what a pane does.
        position: 'relative',
        zIndex: 2,
      }}
    >
      <MobileTab to="/mail" icon="envelope" label="Mail" isActive={active === 'mail'} />
      <MobileTab
        to="/mail/calendar"
        icon="calendar"
        label="Calendar"
        isActive={active === 'calendar'}
      />
    </nav>
  );
}

function MobileTab({
  to,
  icon,
  label,
  isActive,
}: {
  to: '/mail' | '/mail/calendar';
  icon: Parameters<typeof Glyph>[0]['name'];
  label: string;
  isActive: boolean;
}) {
  return (
    <Link
      to={to}
      aria-current={isActive ? 'page' : undefined}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        textDecoration: 'none',
        color: isActive ? 'var(--c-accent-default)' : 'var(--c-fg-tertiary)',
        fontFamily: 'Inter, sans-serif',
        fontSize: 11,
        fontWeight: isActive ? 600 : 500,
        transition: 'color 120ms ease',
      }}
    >
      <Glyph name={icon} size={22} style={{ color: 'currentColor' }} />
      <span>{label}</span>
    </Link>
  );
}

function SuitePill({
  to,
  icon,
  label,
  isActive,
}: {
  to: '/mail' | '/mail/calendar';
  icon: Parameters<typeof Glyph>[0]['name'];
  label: string;
  isActive: boolean;
}) {
  return (
    <Link
      to={to}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 4,
        textDecoration: 'none',
        fontFamily: 'Inter, sans-serif',
        fontSize: 13,
        fontWeight: isActive ? 600 : 500,
        color: isActive ? 'var(--c-fg-default)' : 'var(--c-fg-secondary)',
        background: isActive ? 'var(--c-accent-soft)' : 'transparent',
      }}
    >
      <Glyph
        name={icon}
        size={14}
        style={{
          color: isActive ? 'var(--c-accent-default)' : 'currentColor',
        }}
      />
      {label}
    </Link>
  );
}
